import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { businessDate, systemClock, type BusinessDate } from '@sakhtar/calendar';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2Checkbox,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2Kpi,
  V2Modal,
  V2StatusStrip,
  V2Table,
  V2Tabs,
  type V2GridColumn,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { faDigits } from '../../lib/faDigits.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  formatQuantity,
  isTruncated,
  metaOf,
  pickerOptions,
  stateVariantFor,
  type LabelMeta,
  type Quantity,
} from './inventoryDisplay.js';
import './inventory.css';

interface ReconciliationLedgerDiscrepancy {
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly warehouseId: string;
  readonly warehouseCode: string;
  readonly warehouseLocationId: string;
  readonly lotId: string | null;
  readonly lotCode: string | null;
  readonly serialId: string | null;
  readonly serialNumber: string | null;
  readonly ledgerQty: Quantity;
  readonly balanceQty: Quantity;
  readonly driftQty: Quantity;
}

interface ReconciliationSerialDiscrepancy {
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly warehouseId: string;
  readonly warehouseCode: string;
  readonly availableSerialCount: number;
  readonly onHandQty: Quantity;
  readonly drift: number;
}

interface ReconciliationGLReport {
  readonly glAccountCode: string;
  readonly totalInventoryValuationRial: string;
  readonly glAssetBalanceRial: string;
  readonly varianceRial: string;
  readonly isBalanced: boolean;
}

interface InventoryReconciliationReport {
  readonly runId: string;
  readonly runNumber: string;
  readonly warehouseId: string | null;
  readonly asOfDate: string;
  readonly dryRun: boolean;
  readonly status: 'balanced' | 'discrepant' | 'adjusted';
  readonly ledgerDiscrepancies: readonly ReconciliationLedgerDiscrepancy[];
  readonly serialDiscrepancies: readonly ReconciliationSerialDiscrepancy[];
  readonly glReport: ReconciliationGLReport;
  readonly compensatingMovementIds: readonly string[];
  readonly executedBy: string;
  readonly createdAt: string;
}

type Tab = 'overview' | 'ledger' | 'serials' | 'gl' | 'history';

const STATUS_META: Readonly<Record<string, LabelMeta>> = {
  balanced: { label: 'تراز کامل و منطبق', tone: 'success' },
  discrepant: { label: 'دارای انحراف و مغایرت', tone: 'warn' },
  adjusted: { label: 'تعدیل و جبران‌شده', tone: 'info' },
};

const PICKER_LIMIT = 200;

export function ReconciliationPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { unit, label: amountLabel } = useAmountDisplay();

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [asOfDate, setAsOfDate] = useState<BusinessDate>(systemClock.today());
  const [dryRun, setDryRun] = useState(true);
  const [compensate, setCompensate] = useState(false);
  const [glAccountCode, setGlAccountCode] = useState('1103');

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [latestReport, setLatestReport] = useState<InventoryReconciliationReport | null>(null);

  // Load historical runs
  const historyResult = useApiGet<{ runs: readonly InventoryReconciliationReport[] }>(
    slug ? `${base}/api/v1/inventory/reconciliation/runs` : null,
    [slug],
  );

  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const warehouseOptions = useMemo(
    () =>
      warehouses.status === 'ready' ? pickerOptions(warehouses.data.items, 'warehouseId') : [],
    [warehouses],
  );

  const runs = historyResult.status === 'ready' ? historyResult.data.runs : [];
  const currentReport = latestReport ?? (runs.length > 0 ? runs[0] : null);

  const openRunModal = () => {
    setRunError(null);
    setShowRunModal(true);
  };

  const handleExecute = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const idempotencyKey = `reconcile-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const result = await apiPost<InventoryReconciliationReport>(
        `${base}/api/v1/inventory/reconciliation/run`,
        {
          ...(selectedWarehouseId ? { warehouseId: selectedWarehouseId } : {}),
          asOfDate,
          dryRun,
          compensateDiscrepancies: !dryRun && compensate,
          glAccountCode,
        },
        { headers: { 'idempotency-key': idempotencyKey } },
      );
      setLatestReport(result);
      setShowRunModal(false);
      setActiveTab('overview');
      historyResult.reload();
    } catch (err: unknown) {
      setRunError(bannerMessageFrom(err, 'اجرای مغایرت‌گیری انجام نشد.'));
    } finally {
      setRunning(false);
    }
  };

  const ledgerColumns: V2GridColumn<ReconciliationLedgerDiscrepancy>[] = [
    {
      key: 'product',
      header: 'کالا',
      wrap: true,
      hideable: false,
      render: (d) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{d.productName}</span>
          <span className="inv-cell__sub ltr">{d.productCode}</span>
        </span>
      ),
    },
    { key: 'warehouse', header: 'انبار', render: (d) => d.warehouseCode },
    {
      key: 'tracking',
      header: 'بهر / سریال',
      muted: true,
      render: (d) => <span className="ltr">{d.lotCode ?? d.serialNumber ?? '—'}</span>,
    },
    {
      key: 'ledger',
      header: 'سرجمع کاردکس',
      align: 'end',
      render: (d) => <span className="sk-num">{formatQuantity(d.ledgerQty)}</span>,
    },
    {
      key: 'balance',
      header: 'مانده ثبتی',
      align: 'end',
      render: (d) => <span className="sk-num">{formatQuantity(d.balanceQty)}</span>,
    },
    {
      key: 'drift',
      header: 'انحراف',
      align: 'end',
      emphasis: 'key',
      render: (d) => <span className="sk-num inv-tone-down">{formatQuantity(d.driftQty)}</span>,
    },
  ];

  const serialColumns: V2GridColumn<ReconciliationSerialDiscrepancy>[] = [
    {
      key: 'product',
      header: 'کالا',
      wrap: true,
      hideable: false,
      render: (s) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{s.productName}</span>
          <span className="inv-cell__sub ltr">{s.productCode}</span>
        </span>
      ),
    },
    { key: 'warehouse', header: 'انبار', render: (s) => s.warehouseCode },
    {
      key: 'count',
      header: 'سریال‌های فعال در انبار',
      align: 'end',
      render: (s) => <span className="sk-num">{faDigits(s.availableSerialCount)}</span>,
    },
    {
      key: 'onHand',
      header: 'مانده فیزیکی کل',
      align: 'end',
      render: (s) => <span className="sk-num">{formatQuantity(s.onHandQty)}</span>,
    },
    {
      key: 'drift',
      header: 'کسری / مازاد سریال',
      align: 'end',
      emphasis: 'key',
      render: (s) => <span className="sk-num inv-tone-down">{faDigits(s.drift)}</span>,
    },
  ];

  const historyColumns: V2GridColumn<InventoryReconciliationReport>[] = [
    {
      key: 'number',
      header: 'شمارهٔ جلسه',
      hideable: false,
      render: (r) => <span className="inv-code ltr">{r.runNumber}</span>,
    },
    {
      key: 'date',
      header: 'تاریخ مبنا',
      render: (r) => <span className="sk-num">{displayJalaliDate(r.asOfDate)}</span>,
    },
    {
      key: 'kind',
      header: 'نوع',
      render: (r) => (
        <V2Badge tone={r.dryRun ? 'info' : 'warn'}>{r.dryRun ? 'پیش‌نمایش' : 'اجرای زنده'}</V2Badge>
      ),
    },
    {
      key: 'ledger',
      header: 'مغایرت کاردکس',
      align: 'end',
      render: (r) => <span className="sk-num">{faDigits(r.ledgerDiscrepancies.length)}</span>,
    },
    {
      key: 'serials',
      header: 'مغایرت سریال',
      align: 'end',
      render: (r) => <span className="sk-num">{faDigits(r.serialDiscrepancies.length)}</span>,
    },
    {
      key: 'gl',
      header: amountColumnHeader('مغایرت ارزش', unit),
      align: 'end',
      render: (r) => <span className="sk-num">{formatRialFa(r.glReport.varianceRial, unit)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (r) => {
        const meta = metaOf(STATUS_META, r.status);
        return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
      },
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (r) => (
        <V2Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setLatestReport(r);
            setActiveTab('overview');
          }}
        >
          مشاهده
        </V2Button>
      ),
    },
  ];

  const glColumns: V2TableColumn<ReconciliationGLReport & { id: string }>[] = [
    {
      key: 'account',
      header: 'سرفصل معین حسابداری',
      render: (g) => (
        <span className="inv-cell__main">
          <span className="ltr">{g.glAccountCode}</span> — موجودی کالا و مواد
        </span>
      ),
    },
    {
      key: 'valuation',
      header: amountColumnHeader('ارزش ریالی موجودی انبار', unit),
      align: 'end',
      render: (g) => (
        <span className="sk-num">{formatRialFa(g.totalInventoryValuationRial, unit)}</span>
      ),
    },
    {
      key: 'gl',
      header: amountColumnHeader('مانده ثبت‌شده در دفتر کل', unit),
      align: 'end',
      render: (g) => <span className="sk-num">{formatRialFa(g.glAssetBalanceRial, unit)}</span>,
    },
    {
      key: 'variance',
      header: amountColumnHeader('مغایرت ارزش', unit),
      align: 'end',
      render: (g) => (
        <span className={`sk-num ${g.isBalanced ? '' : 'inv-tone-down'}`}>
          {formatRialFa(g.varianceRial, unit)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت تطبیق',
      render: (g) => (
        <V2Badge tone={g.isBalanced ? 'success' : 'warn'}>
          {g.isBalanced ? 'تراز' : 'دارای انحراف'}
        </V2Badge>
      ),
    },
  ];

  if (!slug) return null;

  const report = currentReport;

  return (
    <div className="inv-page">
      {/* Top Banner / Actions */}
      <V2Card>
        <V2CardHeader
          icon="shield"
          title="مرکز تطبیق و مغایرت‌گیری انبار"
          actions={
            <V2Button variant="primary" size="sm" icon="plus" onClick={openRunModal}>
              اجرای فرایند مغایرت‌گیری
            </V2Button>
          }
        />
        <V2CardBody>
          <p className="inv-form__note">
            این مرکز مغایرت‌های میان سه لایهٔ کلیدی را ممیزی می‌کند: ۱. تطبیق ریاضی سرجمع گردش
            کاردکس با مانده موجودی در انبار ۲. تطبیق سریال‌های ثبت‌شده و قابل‌فروش با موجودی فیزیکی
            ۳. تطبیق ارزش ریالی انبار (میانگین موزون متحرک) با سرفصل دارایی در دفتر کل
          </p>
        </V2CardBody>
      </V2Card>

      {historyResult.status === 'loading' && !latestReport ? <LoadingState /> : null}
      {historyResult.status === 'error' && !latestReport ? (
        <StatePanel
          variant={stateVariantFor(historyResult.error.status)}
          title={
            historyResult.error.status === 403
              ? 'دسترسی مشاهدهٔ مغایرت‌گیری را ندارید'
              : 'سوابق مغایرت‌گیری قابل نمایش نیست'
          }
        />
      ) : null}

      {/* Main Metric & Summary Card */}
      {report ? (
        <V2Card>
          <V2CardHeader
            icon="layers"
            title={`گزارش مغایرت‌گیری: ${report.runNumber}`}
            actions={
              <span className="inv-actions">
                {report.dryRun ? (
                  <V2Badge tone="info">پیش‌نمایش (بدون ثبت)</V2Badge>
                ) : (
                  <V2Badge tone="warn">اجرای زنده (ثبت در دفتر)</V2Badge>
                )}
                <V2Badge tone={metaOf(STATUS_META, report.status).tone}>
                  {metaOf(STATUS_META, report.status).label}
                </V2Badge>
              </span>
            }
          />
          <V2CardBody>
            <div className="inv-form">
              <div className="inv-kpi-grid">
                <V2Kpi
                  label="ارزش ریالی موجودی انبار"
                  value={`${formatRialFa(report.glReport.totalInventoryValuationRial, unit)} ${amountLabel}`}
                />
                <V2Kpi
                  label={`مانده دفتر کل (سرفصل ${faDigits(report.glReport.glAccountCode)})`}
                  value={`${formatRialFa(report.glReport.glAssetBalanceRial, unit)} ${amountLabel}`}
                />
                <div data-tone={report.glReport.isBalanced ? 'success' : 'danger'}>
                  <V2Kpi
                    label="مغایرت انبار با دفتر کل"
                    value={`${formatRialFa(report.glReport.varianceRial, unit)} ${amountLabel}`}
                  />
                </div>
                <div data-tone={report.ledgerDiscrepancies.length > 0 ? 'warn' : 'success'}>
                  <V2Kpi
                    label="انحراف کاردکس از مانده"
                    value={`${report.ledgerDiscrepancies.length.toLocaleString('fa-IR')} قلم`}
                  />
                </div>
                <div data-tone={report.serialDiscrepancies.length > 0 ? 'warn' : 'success'}>
                  <V2Kpi
                    label="مغایرت سریال‌ها"
                    value={`${report.serialDiscrepancies.length.toLocaleString('fa-IR')} قلم`}
                  />
                </div>
              </div>

              {report.compensatingMovementIds.length > 0 ? (
                <V2Alert tone="info">
                  {report.compensatingMovementIds.length.toLocaleString('fa-IR')} سند تعدیل جبرانی
                  رسمی صادر شد.
                </V2Alert>
              ) : null}
            </div>
          </V2CardBody>
        </V2Card>
      ) : historyResult.status === 'ready' ? (
        <V2Card>
          <V2EmptyState
            icon="shield"
            title="هنوز هیچ جلسهٔ مغایرت‌گیری ثبت نشده است"
            description="برای تطبیق اسناد کاردکس، سریال‌ها و انطباق ریالی با دفتر کل، مغایرت‌گیری را اجرا کنید."
            action={
              <V2Button variant="primary" onClick={openRunModal}>
                اجرای اولین مغایرت‌گیری
              </V2Button>
            }
          />
        </V2Card>
      ) : null}

      {/* Tabs */}
      {report ? (
        <V2Card>
          <div className="inv-tabbar">
            <V2Tabs
              items={[
                { id: 'overview', label: 'خلاصه و تحلیل' },
                {
                  id: 'ledger',
                  label: `انحراف کاردکس (${faDigits(report.ledgerDiscrepancies.length)})`,
                },
                {
                  id: 'serials',
                  label: `مغایرت سریال‌ها (${faDigits(report.serialDiscrepancies.length)})`,
                },
                { id: 'gl', label: 'تطبیق با دفتر کل' },
                { id: 'history', label: `تاریخچهٔ جلسات (${faDigits(runs.length)})` },
              ]}
              activeId={activeTab}
              onChange={(id) => setActiveTab(id as Tab)}
            />
          </div>

          {activeTab === 'overview' ? (
            <div className="inv-tabpanel">
              <p className="inv-form__note">
                <strong>وضعیت تطبیق: </strong>
                {report.status === 'balanced'
                  ? 'همهٔ اقلام کاردکس، سریال‌ها و ارزش دارایی انبار با حسابداری و اسناد فیزیکی تطابق کامل دارند.'
                  : 'اختلافاتی در لایه‌های انبار یا دفتر کل شناسایی شد. جزئیات را در تب‌های بالا بررسی کنید.'}
              </p>
              <p className="inv-form__lead">
                تاریخ مبنای گزارش: {displayJalaliDate(report.asOfDate)} | شمارهٔ جلسه:{' '}
                <span className="ltr sk-num">{report.runNumber}</span>
              </p>
            </div>
          ) : null}

          {activeTab === 'ledger' ? (
            report.ledgerDiscrepancies.length === 0 ? (
              <div className="inv-tabpanel">
                <V2StatusStrip
                  tone="success"
                  title="هیچ انحرافی میان گردش کاردکس و مانده ثبتی موجودی وجود ندارد."
                />
              </div>
            ) : (
              <V2DataGrid
                bare
                columns={ledgerColumns}
                rows={report.ledgerDiscrepancies}
                rowKey={(d) =>
                  `${d.productId}:${d.warehouseLocationId}:${d.lotId ?? ''}:${d.serialId ?? ''}`
                }
                storageKey="inventory.reconciliation.ledger"
                zebra
              />
            )
          ) : null}

          {activeTab === 'serials' ? (
            report.serialDiscrepancies.length === 0 ? (
              <div className="inv-tabpanel">
                <V2StatusStrip
                  tone="success"
                  title="شمارش سریال‌های فعال با مانده‌های موجودی تطابق ۱:۱ دارد."
                />
              </div>
            ) : (
              <V2DataGrid
                bare
                columns={serialColumns}
                rows={report.serialDiscrepancies}
                rowKey={(s) => `${s.productId}:${s.warehouseId}`}
                storageKey="inventory.reconciliation.serials"
                zebra
              />
            )
          ) : null}

          {activeTab === 'gl' ? (
            <V2Table
              columns={glColumns}
              rows={[{ ...report.glReport, id: report.runId }]}
              density="comfortable"
            />
          ) : null}

          {activeTab === 'history' ? (
            <V2DataGrid
              bare
              columns={historyColumns}
              rows={runs}
              rowKey={(r) => r.runId}
              selectedKey={report.runId}
              storageKey="inventory.reconciliation.history"
              zebra
              emptyState={<V2EmptyState icon="clock" title="سابقه‌ای ثبت نشده است" compact />}
            />
          ) : null}
        </V2Card>
      ) : null}

      {/* Execution Modal */}
      <V2Modal
        title="اجرای فرایند مغایرت‌گیری انبار و دفاتر کل"
        open={showRunModal}
        onClose={() => setShowRunModal(false)}
        dismissible={!running}
        footer={
          <>
            <V2Button variant="ghost" onClick={() => setShowRunModal(false)} disabled={running}>
              انصراف
            </V2Button>
            <V2Button type="submit" form="reconcile-run-form" variant="primary" disabled={running}>
              {running ? 'در حال بررسی و تطبیق…' : 'شروع مغایرت‌گیری'}
            </V2Button>
          </>
        }
      >
        <form
          id="reconcile-run-form"
          className="inv-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void handleExecute();
          }}
        >
          {runError ? <V2Alert tone="danger">{runError}</V2Alert> : null}

          <V2FormField
            label="محدودهٔ انبار"
            width="full"
            hint={
              warehouses.status === 'ready' &&
              isTruncated(warehouses.data.items.length, warehouses.data.total)
                ? `فقط ${faDigits(PICKER_LIMIT)} انبار اول فهرست نمایش داده می‌شود.`
                : undefined
            }
          >
            <select
              className="sk-select"
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
            >
              <option value="">همهٔ انبارهای فعال</option>
              {warehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </V2FormField>

          <V2FormField label="تاریخ مبنای گزارش" width="full">
            <JalaliDateInput
              value={asOfDate}
              onChange={(d) => setAsOfDate(businessDate(d ?? systemClock.today()))}
            />
          </V2FormField>

          <V2FormField label="کد سرفصل دارایی در دفتر کل" width="full">
            <input
              type="text"
              className="sk-input ltr"
              value={glAccountCode}
              onChange={(e) => setGlAccountCode(e.target.value)}
            />
          </V2FormField>

          <div className="inv-form__group inv-form">
            <V2Checkbox
              label="حالت پیش‌نمایش (بدون ثبت سند اصلاحی)"
              checked={dryRun}
              onCheckedChange={(checked) => {
                setDryRun(checked);
                if (checked) setCompensate(false);
              }}
            />
            {!dryRun ? (
              <V2Checkbox
                label="صدور اسناد تعدیل جبرانی رسمی برای رفع انحرافات مقداری کاردکس"
                description="اسناد صادرشده پس از ثبت قابل ویرایش یا حذف نیستند."
                checked={compensate}
                onCheckedChange={setCompensate}
              />
            ) : null}
          </div>
        </form>
      </V2Modal>
    </div>
  );
}
